import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PH_REGIONS,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import { resolveReceiptFields, type ReceiptFieldFlags } from '@mlm/shared';
import {
  adminResetUserPassword,
  clearManagerPin,
  createBranchCashier,
  getBranchSellable,
  getShippingConfig,
  listBranchCashiers,
  listBranchInventory,
  listBranches,
  listInventoryMovements,
  patchBranchCashier,
  patchBranchCashierStatus,
  patchBranchSellableProduct,
  patchBranchSellableVariant,
  putBranchSellable,
  setBranchCashierPassword,
  setManagerPin,
  updateBranch,
  type Branch,
  type BranchCashier,
  type InventoryBatchKind,
  type ReceiptHeader,
} from '../lib/api';
import { useHasPerm } from '../lib/auth-store';
import { SlotCodePicker } from '../components/slot-code-picker';
import { InventoryBatchDialog } from '../components/branches/inventory-batch-dialog';
import { BatchesSubview } from '../components/branches/batches-subview';

export function BranchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const branchesQuery = useQuery({
    queryKey: ['admin-branches'],
    queryFn: listBranches,
  });

  const branch = branchesQuery.data?.find((b) => b.id === id) ?? null;

  const setEcom = useMutation({
    mutationFn: (ecommerceReady: boolean) =>
      updateBranch(branch!.id, { ecommerceReady }),
    meta: { successMessage: 'E-commerce readiness updated.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-branches'] }),
  });

  if (branchesQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!branch) {
    return (
      <div className="space-y-3">
        <Link to="/branches" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to branches
        </Link>
        <p className="text-sm text-destructive">Branch not found.</p>
      </div>
    );
  }

  const isStockist = branch.type === 'stockist';

  return (
    <div className="space-y-4">
      <div>
        <Link to="/branches" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to branches
        </Link>
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">
            {branch.name}{' '}
            <span className="text-base font-normal text-muted-foreground font-mono">
              {branch.code}
            </span>
          </h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <Badge variant={isStockist ? 'outline' : 'secondary'} className="capitalize">
              {branch.type}
            </Badge>
            {branch.ecommerceReady && <Badge variant="success">E-com ready</Badge>}
            <Badge variant={branch.completionMode === 'manual' ? 'outline' : 'secondary'}>
              POS: {branch.completionMode === 'manual' ? 'Manual release' : 'Auto'}
            </Badge>
          </div>
        </div>
        <label className="text-sm flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={branch.ecommerceReady}
            disabled={setEcom.isPending}
            onCheckedChange={(v) => setEcom.mutate(v === true)}
          />
          E-commerce ready
        </label>
      </div>

      <Tabs defaultValue="products">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="cashiers">POS staff</TabsTrigger>
          <TabsTrigger value="pos">POS &amp; Receipt</TabsTrigger>
          <TabsTrigger value="discounts">Discounts</TabsTrigger>
          <TabsTrigger value="shipping">Shipping</TabsTrigger>
          {isStockist && <TabsTrigger value="owner">Owner</TabsTrigger>}
        </TabsList>

        <TabsContent value="products">
          <ProductsPanel branch={branch} />
        </TabsContent>
        <TabsContent value="cashiers">
          <CashiersPanel branch={branch} />
        </TabsContent>
        <TabsContent value="pos">
          <PosReceiptSettings branch={branch} />
        </TabsContent>
        <TabsContent value="discounts">
          <CashierDiscountSettings branch={branch} />
        </TabsContent>
        <TabsContent value="shipping">
          <div className="space-y-4">
            <ShippingOriginSettings branch={branch} />
            <AreaFulfillmentSettings branch={branch} />
          </div>
        </TabsContent>
        {isStockist && (
          <TabsContent value="owner">
            <StockistOwnerSettings branch={branch} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/**
 * Per-branch product list — the heart of branch admin.
 *
 * Stock view: products table with the sellable toggle + on-hand qty + a "View
 * log" drawer for per-product movement history. Stock changes never happen
 * inline; instead the Products tab header carries [+ Receive shipment] and
 * [− Write off] buttons that open the batch dialog (the "document" model).
 *
 * Batches view: the document log — one row per receive/write-off, printable.
 */
function ProductsPanel({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const [logProductId, setLogProductId] = useState<string | null>(null);
  const [view, setView] = useState<'stock' | 'batches'>('stock');
  const [batchKind, setBatchKind] = useState<InventoryBatchKind | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');

  const inv = useQuery({
    queryKey: ['admin-inventory', branch.id, search, statusFilter],
    queryFn: () => listBranchInventory(branch.id, search.trim() || undefined, statusFilter),
  });
  const sellable = useQuery({
    queryKey: ['admin-branch-sellable', branch.id],
    queryFn: () => getBranchSellable(branch.id),
  });

  const sellableSet = useMemo(
    () => new Set(sellable.data?.productIds ?? []),
    [sellable.data?.productIds],
  );
  const variantSellableSet = useMemo(
    () => new Set(sellable.data?.variantIds ?? []),
    [sellable.data?.variantIds],
  );

  const setMode = useMutation({
    mutationFn: (mode: 'all' | 'subset') => putBranchSellable(branch.id, { mode }),
    meta: { successMessage: 'Product visibility updated.' },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin-branch-sellable', branch.id] }),
  });
  const toggleVariant = useMutation({
    mutationFn: (args: { variantId: string; sellable: boolean }) =>
      patchBranchSellableVariant(branch.id, args.variantId, args.sellable),
    meta: { successMessage: 'Variation visibility updated.' },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['admin-branch-sellable', branch.id] }),
  });
  const toggleSellable = useMutation({
    mutationFn: (args: { productId: string; sellable: boolean }) =>
      patchBranchSellableProduct(branch.id, args.productId, args.sellable),
    meta: { successMessage: 'Product visibility updated.' },
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey: ['admin-branch-sellable', branch.id] });
      const prev = queryClient.getQueryData<{ mode: 'all' | 'subset'; productIds: string[] }>([
        'admin-branch-sellable',
        branch.id,
      ]);
      if (prev) {
        const nextIds = args.sellable
          ? Array.from(new Set([...prev.productIds, args.productId]))
          : prev.productIds.filter((p) => p !== args.productId);
        queryClient.setQueryData(['admin-branch-sellable', branch.id], {
          ...prev,
          productIds: nextIds,
        });
      }
      return { prev };
    },
    onError: (_e, _args, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['admin-branch-sellable', branch.id], ctx.prev);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['admin-branch-sellable', branch.id] }),
  });

  const mode = sellable.data?.mode ?? 'all';
  const logProduct = logProductId
    ? inv.data?.find((r) => r.productId === logProductId) ?? null
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant={view === 'stock' ? 'default' : 'outline'}
            onClick={() => setView('stock')}
          >
            Stock
          </Button>
          <Button
            size="sm"
            variant={view === 'batches' ? 'default' : 'outline'}
            onClick={() => setView('batches')}
          >
            Batches
          </Button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setBatchKind('receive')}>
            + Receive shipment
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-destructive"
            onClick={() => setBatchKind('write_off')}
          >
            − Write off
          </Button>
        </div>
      </div>

      {view === 'batches' ? (
        <BatchesSubview branchId={branch.id} />
      ) : (
        <>
          <div className="rounded-md border bg-muted/30 p-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Product visibility</p>
              <p className="text-xs text-muted-foreground">
                {mode === 'all'
                  ? 'Every active product is sellable here.'
                  : `Only the products you check below are sellable here — ${sellableSet.size} selected.`}
              </p>
              {mode === 'subset' && sellableSet.size === 0 && (
                <p className="mt-1 text-xs text-destructive">
                  Nothing is selected — this branch can't sell anything until you check a product.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {(['all', 'subset'] as const).map((m) => (
                <Button
                  key={m}
                  size="sm"
                  variant={mode === m ? 'default' : 'outline'}
                  disabled={setMode.isPending || mode === m || sellable.isLoading}
                  onClick={() => {
                    // Switching to an empty subset would black out the whole
                    // till, so confirm the intent first.
                    if (m === 'subset' && sellableSet.size === 0) {
                      const ok = window.confirm(
                        'No products are selected yet. Switching to "Selected subset only" makes this branch sell nothing until you check products. Continue?',
                      );
                      if (!ok) return;
                    }
                    setMode.mutate(m);
                  }}
                >
                  {m === 'all' ? 'All active products' : 'Selected subset only'}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products by name or SKU…"
              className="max-w-sm"
            />
            <div className="inline-flex rounded-md border p-0.5 text-xs">
              {(['active', 'archived', 'all'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={
                    'rounded px-3 py-1 capitalize transition-colors ' +
                    (statusFilter === s
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {inv.isLoading || sellable.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : inv.data && inv.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {search.trim()
                ? 'No products match your search.'
                : statusFilter === 'archived'
                  ? 'No archived products.'
                  : 'No products yet.'}
            </p>
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground border-b">
                    <tr>
                      <th className="px-4 py-2 w-24">Sellable</th>
                      <th className="px-4 py-2">SKU</th>
                      <th className="px-4 py-2">Name</th>
                      <th className="px-4 py-2 w-20 text-right">On-hand</th>
                      <th className="px-4 py-2 w-24 text-right" />
                    </tr>
                  </thead>
                  <tbody>
                    {inv.data?.map((row) => (
                      <ProductRow
                        key={row.productId}
                        row={row}
                        mode={mode}
                        sellable={sellableSet.has(row.productId)}
                        variantSellableSet={variantSellableSet}
                        onToggleSellable={(next) =>
                          toggleSellable.mutate({ productId: row.productId, sellable: next })
                        }
                        onToggleVariant={(variantId, next) =>
                          toggleVariant.mutate({ variantId, sellable: next })
                        }
                        onOpenLog={() => setLogProductId(row.productId)}
                      />
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Sheet
        open={!!logProductId}
        onOpenChange={(open) => {
          if (!open) setLogProductId(null);
        }}
      >
        <SheetContent side="right" className="sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>
              Inventory log{logProduct ? ` · ${logProduct.name}` : ''}
            </SheetTitle>
            <SheetDescription>
              {logProduct
                ? `SKU ${logProduct.sku} · current stock ${logProduct.qty}`
                : ''}
            </SheetDescription>
          </SheetHeader>
          {logProductId && (
            <div className="mt-4">
              <ProductInventoryLog branchId={branch.id} productId={logProductId} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      <InventoryBatchDialog
        open={batchKind !== null}
        onOpenChange={(open) => {
          if (!open) setBatchKind(null);
        }}
        branchId={branch.id}
        branchCode={branch.code}
        branchName={branch.name}
        kind={batchKind ?? 'receive'}
        onCreated={(batch) => {
          window.open(
            `/branches/${branch.id}/batches/${batch.id}/print`,
            '_blank',
            'noopener',
          );
        }}
      />
    </div>
  );
}

function ProductRow({
  row,
  mode,
  sellable,
  variantSellableSet,
  onToggleSellable,
  onToggleVariant,
  onOpenLog,
}: {
  row: {
    productId: string;
    sku: string;
    name: string;
    active: boolean;
    qty: number;
    variants: { variantId: string; name: string; sku: string; qty: number }[];
  };
  mode: 'all' | 'subset';
  sellable: boolean;
  variantSellableSet: Set<string>;
  onToggleSellable: (next: boolean) => void;
  onToggleVariant: (variantId: string, next: boolean) => void;
  onOpenLog: () => void;
}) {
  const hasVariants = row.variants.length > 0;
  // Forgiving rule: with the product sellable but NO variant explicitly ticked,
  // ALL variations sell. Ticking any one restricts to the ticked set. Mirror
  // that here so the admin sees the effective state.
  const anyVariantTicked = row.variants.some((v) => variantSellableSet.has(v.variantId));
  const showVariantChecks = mode === 'subset' && sellable && hasVariants;
  // For a variant-bearing product the sellable stock lives per-variant; the
  // product-level count is a placeholder POS ignores, so show the sum.
  const displayQty = hasVariants ? row.variants.reduce((s, v) => s + v.qty, 0) : row.qty;
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="px-4 py-2 align-middle">
        {mode === 'subset' ? (
          <Checkbox
            checked={sellable}
            onCheckedChange={(v) => onToggleSellable(v === true)}
            aria-label={sellable ? 'Sellable' : 'Not sellable'}
          />
        ) : (
          <Badge variant="secondary">Sellable</Badge>
        )}
      </td>
      <td className="px-4 py-2 font-mono text-xs align-middle">{row.sku}</td>
      <td className="px-4 py-2 align-middle">
        <span className={row.active ? '' : 'text-muted-foreground line-through'}>{row.name}</span>
        {!row.active && (
          <Badge variant="outline" className="ml-2 text-[10px]">
            Archived
          </Badge>
        )}
        {hasVariants && (
          <div className="mt-1 space-y-0.5">
            {showVariantChecks && !anyVariantTicked && (
              <p className="text-[10px] text-muted-foreground italic">
                All variations sellable. Tick specific ones to restrict.
              </p>
            )}
            {row.variants.map((v) => (
              <div key={v.variantId} className="flex items-center gap-2 text-xs text-muted-foreground">
                {showVariantChecks && (
                  <Checkbox
                    checked={anyVariantTicked ? variantSellableSet.has(v.variantId) : true}
                    onCheckedChange={(c) => onToggleVariant(v.variantId, c === true)}
                    aria-label={`Sellable: ${v.name}`}
                    className="h-3.5 w-3.5"
                  />
                )}
                <span className="font-mono">{v.sku}</span>
                <span>{v.name}</span>
                <span className="ml-auto tabular-nums">on-hand {v.qty}</span>
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-2 font-medium align-middle tabular-nums text-right">
        {displayQty}
        {hasVariants && <div className="text-[10px] font-normal text-muted-foreground">across variations</div>}
      </td>
      <td className="px-4 py-2 text-right">
        <Button size="sm" variant="ghost" onClick={onOpenLog} title="View movement log">
          View log
        </Button>
      </td>
    </tr>
  );
}

const REASON_LABEL: Record<string, string> = {
  receive: 'Receive',
  adjustment: 'Adjustment',
  write_off: 'Write-Off',
  void: 'Void',
  pos_sale: 'POS sale',
  ecom_sale: 'Ecom sale',
  refund_restock: 'Refund restock',
  cancel_restock: 'Cancel restock',
};

/**
 * Movement history scoped to one product on one branch. Rendered inside the
 * Sheet drawer opened from the Products tab.
 */
function ProductInventoryLog({
  branchId,
  productId,
}: {
  branchId: string;
  productId: string;
}) {
  const logs = useQuery({
    queryKey: ['admin-inventory-logs', branchId, productId],
    queryFn: () => listInventoryMovements(branchId, { productId }),
  });
  if (logs.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if ((logs.data?.length ?? 0) === 0)
    return <p className="text-sm text-muted-foreground">No movements yet.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs text-muted-foreground border-b">
        <tr>
          <th className="py-2 pr-2">When</th>
          <th className="py-2 pr-2">Reason</th>
          <th className="py-2 pr-2 text-right">Δ</th>
          <th className="py-2 pr-2 text-right">Before → After</th>
          <th className="py-2 pr-2">Memo / Ref</th>
        </tr>
      </thead>
      <tbody>
        {logs.data!.map((m) => (
          <tr key={m.id} className="border-b">
            <td className="py-2 pr-2 text-xs text-muted-foreground whitespace-nowrap">
              {new Date(m.createdAt).toLocaleString()}
            </td>
            <td className="py-2 pr-2">
              {REASON_LABEL[m.reason] ?? m.reason}
              {m.variantName && (
                <div className="text-[11px] text-muted-foreground">{m.variantName}</div>
              )}
            </td>
            <td
              className={`py-2 pr-2 text-right font-medium ${
                m.delta < 0 ? 'text-destructive' : 'text-success'
              }`}
            >
              {m.delta > 0 ? `+${m.delta}` : m.delta}
            </td>
            <td className="py-2 pr-2 text-right tabular-nums text-muted-foreground">
              {m.balanceBefore} →{' '}
              <span className="text-foreground font-medium">{m.balanceAfter}</span>
            </td>
            <td className="py-2 pr-2 text-xs text-muted-foreground">
              {m.note ?? (m.refType ? m.refType : '')}
              {m.note && m.refType ? ` · ${m.refType}` : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * BF3 — per-branch POS completion mode + tape receipt header. Auto = the
 * legacy instant checkout; Manual routes paid orders to the Releasing queue.
 */
function PosReceiptSettings({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-branches'] });

  const setMode = useMutation({
    mutationFn: (completionMode: 'auto' | 'manual') => updateBranch(branch.id, { completionMode }),
    meta: { successMessage: 'Completion mode saved.' },
    onSuccess: invalidate,
  });

  const [header, setHeader] = useState<ReceiptHeader>(branch.receiptHeader ?? {});
  const saveHeader = useMutation({
    mutationFn: () => updateBranch(branch.id, { receiptHeader: header }),
    meta: { successMessage: 'Receipt header saved.' },
    onSuccess: invalidate,
  });

  const field = (key: keyof ReceiptHeader, label: string, placeholder?: string) => (
    <label className="block text-sm">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Input
        value={header[key] ?? ''}
        placeholder={placeholder}
        onChange={(e) => setHeader((h) => ({ ...h, [key]: e.target.value }))}
      />
    </label>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Completion mode</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Auto completes the sale at checkout. Manual parks the paid order in the Releasing
            queue. The cashier hands over goods + kit codes and releases it there.
          </p>
          <div className="flex gap-2">
            {(['auto', 'manual'] as const).map((m) => (
              <Button
                key={m}
                size="sm"
                variant={branch.completionMode === m ? 'default' : 'outline'}
                disabled={setMode.isPending || branch.completionMode === m}
                onClick={() => setMode.mutate(m)}
              >
                {m === 'auto' ? 'Auto (instant)' : 'Manual (releasing area)'}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Receipt header</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Printed at the top of the cashier tape.
          </p>
          {field('companyName', 'Company name', branch.name)}
          {field('address', 'Address')}
          {field('phone', 'Phone')}
          {field('tin', 'TIN')}
          {field('footer', 'Footer note')}
          <Button size="sm" onClick={() => saveHeader.mutate()} disabled={saveHeader.isPending}>
            {saveHeader.isPending ? 'Saving…' : 'Save receipt header'}
          </Button>
        </CardContent>
      </Card>

      <ReceiptFieldsSettings branch={branch} />

      <OrderNumberingSettings branch={branch} />
    </div>
  );
}

/**
 * Which optional blocks the cashier tape prints. Per-branch rather than a
 * tenant setting because the two things already shaping the tape (the company
 * header above, and the order-number prefix below) are per-branch too — a
 * stockist's slip and the main store's may legitimately differ.
 *
 * Every flag defaults ON except Remarks; the stored blob is sparse and merged
 * onto the defaults at render time, so an unconfigured branch prints
 * everything rather than a blank tape.
 */
const RECEIPT_FIELD_ROWS: Array<{ key: keyof ReceiptFieldFlags; label: string; hint: string }> = [
  { key: 'showMemberName', label: 'Customer name', hint: 'The member the sale was rung up for.' },
  { key: 'showCashierName', label: 'Cashier name', hint: 'The staff account that processed it.' },
  {
    key: 'showBranchName',
    label: 'Branch name',
    hint: 'The branch/stockist name. Separate from the order-number prefix, which always prints.',
  },
  { key: 'showDateTime', label: 'Date & time', hint: 'Printed as two rows.' },
  {
    key: 'showRemarks',
    label: 'Remarks',
    hint: 'The POS remark on the sale. Off by default — it is an internal memo.',
  },
];

function ReceiptFieldsSettings({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<ReceiptFieldFlags>(() =>
    resolveReceiptFields(branch.receiptFields),
  );
  const save = useMutation({
    mutationFn: () => updateBranch(branch.id, { receiptFields: fields }),
    meta: { successMessage: 'Receipt fields saved.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-branches'] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Receipt fields</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          What the cashier tape prints below the order number. Line items, totals, payment and
          activation codes always print.
        </p>
        {RECEIPT_FIELD_ROWS.map((row) => (
          <label key={row.key} className="flex items-start gap-2 cursor-pointer">
            <Checkbox
              checked={fields[row.key]}
              onCheckedChange={(v) => setFields((f) => ({ ...f, [row.key]: v === true }))}
              className="mt-0.5"
            />
            <span className="space-y-0.5">
              <span className="block text-sm">{row.label}</span>
              <span className="block text-xs text-muted-foreground">{row.hint}</span>
            </span>
          </label>
        ))}
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save receipt fields'}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Per-branch order-number series. With a prefix set, orders at this branch get
 * human-readable codes like SO-00001 from an atomic counter; clearing the
 * prefix falls back to random codes. Every branch runs its OWN series, so two
 * branches may share a prefix (each issues its own SO-00001). Editing the start
 * number resumes the counter from there (use with care — lowering below
 * already-issued numbers risks a clash on this branch's codes, which the
 * server rejects).
 */
function OrderNumberingSettings({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const [prefix, setPrefix] = useState(branch.orderPrefix ?? '');
  // `start` is left blank unless the admin wants to RESET the series; the live
  // counter (`orderNextSeq`) is shown read-only below. Seeding this from
  // `orderSeqStart` and always sending it back would rewind a live counter that
  // has since advanced (re-issuing an already-printed number).
  const [start, setStart] = useState('');
  const [pad, setPad] = useState(String(branch.orderSeqPad ?? 5));
  const [err, setErr] = useState<string | null>(null);
  const [rewind, setRewind] = useState<number | null>(null);

  const cleanPrefix = prefix.trim() ? prefix.trim().toUpperCase() : null;
  const padNum = Math.min(12, Math.max(1, Number(pad) || 5));

  const runSave = (allowRewind: boolean) => {
    // Send only fields the admin actually changed — a blank Start number leaves
    // the live counter alone.
    const payload: Parameters<typeof updateBranch>[1] = {};
    if (cleanPrefix !== (branch.orderPrefix ?? null)) payload.orderPrefix = cleanPrefix;
    if (padNum !== branch.orderSeqPad) payload.orderSeqPad = padNum;
    if (start.trim() !== '') payload.orderSeqStart = Math.max(1, Number(start) || 1);
    if (allowRewind) payload.allowRewind = true;
    if (Object.keys(payload).length === 0) {
      setErr('Nothing changed.');
      return;
    }
    save.mutate(payload);
  };

  const save = useMutation({
    mutationFn: (payload: Parameters<typeof updateBranch>[1]) => updateBranch(branch.id, payload),
    meta: { successMessage: 'Order numbering saved.' },
    onSuccess: () => {
      setErr(null);
      setRewind(null);
      setStart('');
      queryClient.invalidateQueries({ queryKey: ['admin-branches'] });
    },
    onError: (e: unknown) => {
      const resp = (e as { response?: { data?: { error?: string; maxIssued?: number } } }).response?.data;
      if (resp?.error === 'order_seq_start_below_issued') {
        setRewind(resp.maxIssued ?? 0);
        setErr(null);
      } else {
        setRewind(null);
        setErr('Could not save.');
      }
    },
  });

  const nextCode = branch.orderPrefix
    ? `${branch.orderPrefix}${String(branch.orderNextSeq).padStart(branch.orderSeqPad, '0')}`
    : '(random codes)';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Order numbering</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Give this branch its own order-number series (branches can share a
          prefix — each runs its own count).
        </p>
        <p className="text-sm">
          Next order number:{' '}
          <span className="font-mono font-medium">{nextCode}</span>
        </p>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Prefix (blank = random)</span>
          <Input value={prefix} maxLength={8} placeholder="SO-" onChange={(e) => setPrefix(e.target.value.toUpperCase())} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            <span className="text-xs text-muted-foreground">Reset start number (blank = leave)</span>
            <Input type="number" min="1" value={start} placeholder="—" onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="text-xs text-muted-foreground">Digits (padding)</span>
            <Input type="number" min="1" max="12" value={pad} onChange={(e) => setPad(e.target.value)} />
          </label>
        </div>
        {err && <p className="text-xs text-destructive">{err}</p>}
        {rewind !== null && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            This branch has already issued number {rewind}. Starting at or below it
            would re-use an existing order code and break the next checkout.
            <div className="mt-1">
              <Button size="sm" variant="destructive" onClick={() => runSave(true)} disabled={save.isPending}>
                Force reset anyway
              </Button>
            </div>
          </div>
        )}
        <Button size="sm" onClick={() => runSave(false)} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save order numbering'}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Per-branch cashier-discount policy. Different branches often grant different
 * override authority — bigger flagship store gets a higher cap, mall kiosk
 * gets none. The cashier can apply a discount up to the cap without a PIN;
 * anything above either cap requires a manager PIN at checkout.
 */
function CashierDiscountSettings({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(branch.cashierDiscountEnabled);
  const [maxPct, setMaxPct] = useState(branch.cashierDiscountMaxPct ?? '0');
  const [maxAmount, setMaxAmount] = useState(branch.cashierDiscountMaxAmount ?? '0');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      updateBranch(branch.id, {
        cashierDiscountEnabled: enabled,
        cashierDiscountMaxPct: String(Number(maxPct) || 0),
        cashierDiscountMaxAmount: String(Number(maxAmount) || 0),
      }),
    meta: { successMessage: 'Cashier discount saved.' },
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      queryClient.invalidateQueries({ queryKey: ['admin-branch', branch.id] });
      queryClient.invalidateQueries({ queryKey: ['branches'] });
    },
  });

  const dirty =
    enabled !== branch.cashierDiscountEnabled ||
    Number(maxPct) !== Number(branch.cashierDiscountMaxPct ?? '0') ||
    Number(maxAmount) !== Number(branch.cashierDiscountMaxAmount ?? '0');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cashier discount</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-xl text-sm">
        <p className="text-xs text-muted-foreground">
          When enabled, the cashier can apply an extra order-level discount
          at checkout on top of membership / rank / stockist discounts. Above
          either cap, a manager PIN is required to approve.
        </p>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(!!v)} />
          Enable cashier-applied discounts
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="cdmaxpct">
              Max % without approval
            </label>
            <Input
              id="cdmaxpct"
              type="number"
              inputMode="decimal"
              value={maxPct}
              onChange={(e) => setMaxPct(e.target.value)}
              min={0}
              max={100}
              disabled={!enabled}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="cdmaxamt">
              Max ₱ without approval
            </label>
            <Input
              id="cdmaxamt"
              type="number"
              inputMode="decimal"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              min={0}
              disabled={!enabled}
            />
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Tip: a cap of <span className="font-mono">0</span> means every
          discount needs a manager PIN. A cap of <span className="font-mono">100</span>%
          / a high ₱ amount means no PIN is ever required.
        </p>

        <div className="flex items-center gap-2 pt-2">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {saved && <span className="text-xs text-success">Saved</span>}
          {save.error && (
            <span className="text-xs text-destructive">Failed to save.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Set the shipping origin zone for this branch. The zone is referenced from
 * the active shipping config (Admin → Shipping). Without an origin, storefront
 * shipping quotes for orders from this branch fail with `branch_origin_unresolved`.
 */
function ShippingOriginSettings({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const cfgQ = useQuery({ queryKey: ['admin-shipping-config'], queryFn: getShippingConfig });
  const [zoneId, setZoneId] = useState<string>(branch.shippingOrigin ?? '');

  const save = useMutation({
    mutationFn: () => updateBranch(branch.id, { shippingOrigin: zoneId || null }),
    meta: { successMessage: 'Shipping origin saved.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-branches'] }),
  });

  if (cfgQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  const zones = cfgQ.data?.zones ?? [];
  if (zones.length === 0) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-sm text-muted-foreground max-w-md">
            No shipping zones configured yet. Set them up in{' '}
            <span className="font-medium">Shipping</span>, then come back here to pick the origin
            for this branch.
          </p>
        </CardContent>
      </Card>
    );
  }

  const dirty = zoneId !== (branch.shippingOrigin ?? '');
  const orphaned = !!branch.shippingOrigin && !zones.some((z) => z.id === branch.shippingOrigin);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Shipping origin zone</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 max-w-md">
        <p className="text-xs text-muted-foreground">
          When a customer orders from this branch, this is where the parcel ships FROM. Required:
          without it, the storefront returns{' '}
          <code className="font-mono">branch_origin_unresolved</code>.
        </p>
        <Select
          value={zoneId || '__none__'}
          onValueChange={(v) => setZoneId(v === '__none__' ? '' : v)}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {zones.map((z) => (
              <SelectItem key={z.id} value={z.id}>
                {z.label} ({z.id})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {orphaned && (
          <p className="text-xs text-warning">
            Current value <code className="font-mono">{branch.shippingOrigin}</code> isn't in the
            active shipping config. Pick a real zone above and save.
          </p>
        )}
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save shipping origin'}
        </Button>
        {save.error && <p className="text-xs text-destructive">Couldn't save.</p>}
      </CardContent>
    </Card>
  );
}

/**
 * Order-by-Area — tag which courier zones (service areas) this branch/stockist
 * FULFILLS, and optionally mark it the Main (fallback) branch. When the tenant's
 * "Order by Area" feature is on, an ecom ship order routes its fulfilling branch
 * to the branch tagged for the buyer's zone (else the Main branch).
 */
function AreaFulfillmentSettings({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const [areas, setAreas] = useState<string[]>(branch.serviceAreas ?? []);
  const [isMain, setIsMain] = useState<boolean>(branch.isMain);

  const save = useMutation({
    mutationFn: () => updateBranch(branch.id, { serviceAreas: areas, isMain }),
    meta: { successMessage: 'Service areas saved.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-branches'] }),
  });

  const toggleArea = (id: string, on: boolean) =>
    setAreas((prev) => (on ? [...new Set([...prev, id])] : prev.filter((z) => z !== id)));

  const sameAreas =
    areas.length === (branch.serviceAreas?.length ?? 0) &&
    areas.every((a) => (branch.serviceAreas ?? []).includes(a));
  const dirty = !sameAreas || isMain !== branch.isMain;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Order-by-Area fulfillment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 max-w-md">
        <p className="text-xs text-muted-foreground">
          When <span className="font-medium">Order by Area</span> is on (Settings), an ecom ship
          order is fulfilled by the branch that covers the buyer&apos;s region — else the Main
          branch.
        </p>
        <div className="space-y-2">
          <label className="text-sm font-medium">Service regions (this branch fulfills)</label>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {PH_REGIONS.map((r) => (
              <label key={r.code} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={areas.includes(r.code)}
                  onCheckedChange={(v) => toggleArea(r.code, v === true)}
                />
                <span>
                  {r.name} <span className="text-muted-foreground font-mono">({r.code})</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm pt-1">
          <Checkbox checked={isMain} onCheckedChange={(v) => setIsMain(v === true)} />
          <span>
            Main branch <span className="text-muted-foreground">— fallback fulfiller when no branch covers a region (only one branch can be Main)</span>
          </span>
        </label>
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save areas'}
        </Button>
        {save.error && <p className="text-xs text-destructive">Couldn&apos;t save.</p>}
      </CardContent>
    </Card>
  );
}

/**
 * F1 — assign the member who owns this stockist branch. When that member buys
 * (ecom/POS), the goods land in this branch's inventory (`stockist_purchase`).
 */
function StockistOwnerSettings({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();

  const setOwner = useMutation({
    mutationFn: (ownerSlotCode: string | null) => updateBranch(branch.id, { ownerSlotCode }),
    meta: { successMessage: 'Stockist owner updated.' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-branches'] });
    },
  });

  const untagged = !branch.ownerSlotId;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Stockist owner</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-md">
        <p className="text-sm text-muted-foreground">
          The <strong>slot</strong> that owns this stockist. In the cashier's{' '}
          <strong>Sell to Stockist</strong> flow, goods transfer into this branch's inventory as a{' '}
          <code className="font-mono">stockist_purchase</code> movement.
        </p>

        {untagged && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Owner slot not set — this stockist can't be sold to at the till until you tag a slot
            below. (Branches whose owner had no active slot at migration time land here.)
          </p>
        )}

        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Owner slot</span>
          <SlotCodePicker
            value={branch.ownerSlotCode}
            onChange={(code) => setOwner.mutate(code)}
            disabled={setOwner.isPending}
            placeholder="Enter the owner's slot code…"
          />
        </div>
        {setOwner.isError && (
          <p className="text-xs text-destructive">Couldn't update owner. Check the slot code.</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Branch-scoped cashier management. Lists POS staff scoped to this branch and
 * exposes a create dialog + per-row deactivate. Backed by the dedicated
 * `/branches/:id/cashiers` routes (gated by the `cashier.provision` perm).
 */
function CashiersPanel({ branch }: { branch: Branch }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BranchCashier | null>(null);
  const [resetTarget, setResetTarget] = useState<BranchCashier | null>(null);

  const list = useQuery({
    queryKey: ['admin-branch-cashiers', branch.id],
    queryFn: () => listBranchCashiers(branch.id),
  });
  const setStatus = useMutation({
    mutationFn: (args: { userId: string; status: 'active' | 'suspended' }) =>
      patchBranchCashierStatus(branch.id, args.userId, args.status),
    meta: { successMessage: 'Staff status updated.' },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin-branch-cashiers', branch.id] }),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">POS staff</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Cashier &amp; manager accounts scoped to{' '}
            <span className="font-medium">{branch.name}</span>. Both are POS-only; neither can
            use the member portal.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          + New staff
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {list.isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : (list.data?.length ?? 0) === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No cashiers yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">TOTP</th>
                <th className="px-4 py-2 font-medium">PIN</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2 font-medium w-72">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.map((c) => (
                <CashierRow
                  key={c.id}
                  cashier={c}
                  onSetStatus={(next) => setStatus.mutate({ userId: c.id, status: next })}
                  submitting={setStatus.isPending}
                  onEdit={() => setEditing(c)}
                  onResetPassword={() => setResetTarget(c)}
                  onPinChanged={() =>
                    queryClient.invalidateQueries({
                      queryKey: ['admin-branch-cashiers', branch.id],
                    })
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <CreateCashierDialog
        open={creating}
        onOpenChange={setCreating}
        branchId={branch.id}
        onCreated={() =>
          queryClient.invalidateQueries({ queryKey: ['admin-branch-cashiers', branch.id] })
        }
      />
      <EditCashierDialog
        cashier={editing}
        branchId={branch.id}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['admin-branch-cashiers', branch.id] });
        }}
      />
      <ResetCashierPasswordDialog
        target={resetTarget}
        branchId={branch.id}
        onClose={() => setResetTarget(null)}
      />
    </Card>
  );
}

function CashierRow({
  cashier,
  onSetStatus,
  submitting,
  onEdit,
  onResetPassword,
  onPinChanged,
}: {
  cashier: BranchCashier;
  onSetStatus: (status: 'active' | 'suspended') => void;
  submitting: boolean;
  onEdit: () => void;
  onResetPassword: () => void;
  onPinChanged: () => void;
}) {
  const isSuspended = cashier.status === 'suspended';
  const isManager = cashier.role === 'manager';
  const [pinMode, setPinMode] = useState(false);
  // The PIN endpoints require `user.write`; the whole panel already requires
  // `cashier.provision`, so a role holding only that would 403 on a PIN click.
  const canManagePin = useHasPerm('user.write');
  return (
    <>
      <tr className="border-b last:border-0">
        <td className="px-4 py-2">{cashier.fullName ?? ''}</td>
        <td className="px-4 py-2">
          <Badge variant={isManager ? 'default' : 'outline'} className="capitalize">
            {cashier.role}
          </Badge>
        </td>
        <td className="px-4 py-2">{cashier.email}</td>
        <td className="px-4 py-2">
          <Badge variant={isSuspended ? 'destructive' : 'secondary'}>{cashier.status}</Badge>
        </td>
        <td className="px-4 py-2 text-xs text-muted-foreground">
          {cashier.totpEnabled ? 'enrolled' : 'not enrolled'}
        </td>
        <td className="px-4 py-2 text-xs">
          {!isManager ? (
            <span className="text-muted-foreground">—</span>
          ) : cashier.posManagerPinSet ? (
            <Badge variant="success" className="text-[10px]">set</Badge>
          ) : (
            <span className="text-muted-foreground">not set</span>
          )}
        </td>
        <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
          {new Date(cashier.createdAt).toLocaleDateString()}
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button size="sm" variant="outline" disabled={submitting || pinMode} onClick={onEdit}>
              Edit…
            </Button>
            {isManager && canManagePin && (
              <Button
                size="sm"
                variant="outline"
                disabled={pinMode || submitting}
                onClick={() => setPinMode(true)}
              >
                {cashier.posManagerPinSet ? 'Replace PIN…' : 'Set PIN…'}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={submitting || pinMode}
              onClick={onResetPassword}
            >
              Password…
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={submitting || pinMode}
              onClick={() => onSetStatus(isSuspended ? 'active' : 'suspended')}
            >
              {isSuspended ? 'Reactivate' : 'Deactivate'}
            </Button>
          </div>
        </td>
      </tr>
      {pinMode && (
        <tr className="border-b last:border-0 bg-muted/30">
          <td colSpan={8} className="px-4 py-3">
            <PinInlineForm
              cashier={cashier}
              onClose={() => setPinMode(false)}
              onSaved={() => {
                setPinMode(false);
                onPinChanged();
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Inline PIN editor — same row as the staff member; collapses on cancel/save.
 * No navigation, no nested pages. Mirrors the rejection-reason inline pattern
 * used on the Payments page. The cashier verifies this manager's email + PIN
 * at the counter to approve above-cap discounts.
 */
function PinInlineForm({
  cashier,
  onClose,
  onSaved,
}: {
  cashier: BranchCashier;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => setManagerPin(cashier.id, pin),
    meta: { successMessage: 'Manager PIN set.' },
    onSuccess: () => onSaved(),
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { error?: string; message?: string } } }).response?.data;
      setErr(data?.message ?? data?.error ?? 'Failed to set PIN.');
    },
  });

  const clear = useMutation({
    mutationFn: async () => clearManagerPin(cashier.id),
    meta: { successMessage: 'Manager PIN cleared.' },
    onSuccess: () => onSaved(),
  });

  function submit() {
    setErr(null);
    if (!/^\d{4,8}$/.test(pin)) { setErr('PIN must be 4–8 digits.'); return; }
    if (pin !== confirmPin) { setErr('PINs do not match.'); return; }
    save.mutate();
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="space-y-1 min-w-40">
        <label className="text-[10px] text-muted-foreground">New PIN (4–8 digits)</label>
        <Input
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          autoFocus
        />
      </div>
      <div className="space-y-1 min-w-40">
        <label className="text-[10px] text-muted-foreground">Confirm</label>
        <Input
          type="password"
          inputMode="numeric"
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <Button size="sm" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save PIN'}
        </Button>
        <Button size="sm" variant="outline" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        {cashier.posManagerPinSet && (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={() => clear.mutate()}
            disabled={clear.isPending}
          >
            {clear.isPending ? 'Clearing…' : 'Clear PIN'}
          </Button>
        )}
      </div>
      {err && <p className="w-full text-xs text-destructive">{err}</p>}
    </div>
  );
}

function CreateCashierDialog({
  open,
  onOpenChange,
  branchId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branchId: string;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  // QoL Slice 5b — role picker; cashier (default) or manager. Manager
  // additionally holds pos.discount.override so they can sign off above-cap
  // cashier discounts via PIN.
  const [role, setRole] = useState<'cashier' | 'manager'>('cashier');

  const reset = () => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setPhone('');
    setRole('cashier');
  };

  const create = useMutation({
    mutationFn: () =>
      createBranchCashier(branchId, {
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
        role,
      }),
    meta: { successMessage: 'POS staff created.' },
    onSuccess: () => {
      onCreated();
      reset();
      onOpenChange(false);
    },
  });

  const errMsg = (create.error as { response?: { data?: { error?: string } } } | null)
    ?.response?.data?.error;
  const valid =
    email.includes('@') && password.length >= 8 && firstName.trim() && lastName.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New POS staff</DialogTitle>
          <DialogDescription>
            POS-only account scoped to this branch. The staff member sets up TOTP on first login.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !create.isPending) create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Role</span>
            <div className="grid grid-cols-2 gap-2">
              {(['cashier', 'manager'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={
                    'rounded-md border px-3 py-2 text-sm text-left capitalize ' +
                    (role === r
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'hover:bg-accent')
                  }
                >
                  <div className="font-medium">{r}</div>
                  <div className="text-[10px] text-muted-foreground normal-case">
                    {r === 'cashier'
                      ? 'Rings up sales, releases orders.'
                      : 'Cashier perms + approves above-cap discounts via PIN.'}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">First name</span>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">Last name</span>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </label>
          </div>
          <label className="space-y-1 text-sm block">
            <span className="text-xs text-muted-foreground">Email</span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="space-y-1 text-sm block">
            <span className="text-xs text-muted-foreground">Temporary password (min 8 chars)</span>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <label className="space-y-1 text-sm block">
            <span className="text-xs text-muted-foreground">Phone (optional)</span>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>
          {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? 'Creating…' : `Create ${role}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Edit an existing POS staffer: name/phone/email + cashier↔manager role swap.
 * Mirrors CreateCashierDialog; sends ONLY the changed fields to the same
 * branch-scoped PATCH. Demoting a manager clears their discount-override PIN
 * server-side, so the dialog calls that out before saving.
 */
function EditCashierDialog({
  cashier,
  branchId,
  onClose,
  onSaved,
}: {
  cashier: BranchCashier | null;
  branchId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'cashier' | 'manager'>('cashier');
  // Prefill on open (keyed remount per cashier keeps this simple).
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (cashier && seededFor !== cashier.id) {
    setFirstName(cashier.firstName ?? '');
    setLastName(cashier.lastName ?? '');
    setEmail(cashier.email);
    setPhone(cashier.phone ?? '');
    setRole(cashier.role);
    setSeededFor(cashier.id);
  }

  const save = useMutation({
    mutationFn: () => {
      const patch: Parameters<typeof patchBranchCashier>[2] = {};
      if (firstName.trim() && firstName.trim() !== (cashier!.firstName ?? '')) {
        patch.firstName = firstName.trim();
      }
      if (lastName.trim() && lastName.trim() !== (cashier!.lastName ?? '')) {
        patch.lastName = lastName.trim();
      }
      if (email.trim().toLowerCase() !== cashier!.email.toLowerCase()) {
        patch.email = email.trim();
      }
      if ((phone.trim() || null) !== (cashier!.phone ?? null)) {
        patch.phone = phone.trim() || null;
      }
      if (role !== cashier!.role) patch.role = role;
      return patchBranchCashier(branchId, cashier!.id, patch);
    },
    meta: { successMessage: 'Staff updated.' },
    onSuccess: () => {
      setSeededFor(null);
      onSaved();
    },
  });

  const errMsg = (save.error as { response?: { data?: { error?: string } } } | null)?.response
    ?.data?.error;
  const demoting = cashier?.role === 'manager' && role === 'cashier';
  const dirty =
    !!cashier &&
    (firstName.trim() !== (cashier.firstName ?? '') ||
      lastName.trim() !== (cashier.lastName ?? '') ||
      email.trim().toLowerCase() !== cashier.email.toLowerCase() ||
      (phone.trim() || null) !== (cashier.phone ?? null) ||
      role !== cashier.role);
  const valid = email.includes('@') && firstName.trim() && lastName.trim();

  function close() {
    if (save.isPending) return;
    setSeededFor(null);
    save.reset();
    onClose();
  }

  return (
    <Dialog open={!!cashier} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit POS staff</DialogTitle>
          <DialogDescription>
            Changes apply immediately; the staff member keeps their current session.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty && valid && !save.isPending) save.mutate();
          }}
        >
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Role</span>
            <div className="grid grid-cols-2 gap-2">
              {(['cashier', 'manager'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={
                    'rounded-md border px-3 py-2 text-sm text-left capitalize ' +
                    (role === r ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent')
                  }
                >
                  <div className="font-medium">{r}</div>
                  <div className="text-[10px] text-muted-foreground normal-case">
                    {r === 'cashier'
                      ? 'Rings up sales, releases orders.'
                      : 'Cashier perms + approves above-cap discounts via PIN.'}
                  </div>
                </button>
              ))}
            </div>
            {demoting && (
              <p className="text-xs text-amber-600 dark:text-amber-500">
                Demoting to cashier clears this manager's discount-override PIN.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">First name</span>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">Last name</span>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </label>
          </div>
          <label className="space-y-1 text-sm block">
            <span className="text-xs text-muted-foreground">Email (login)</span>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="space-y-1 text-sm block">
            <span className="text-xs text-muted-foreground">Phone (optional)</span>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>
          {errMsg && (
            <p className="text-sm text-destructive">
              {errMsg === 'email_taken' ? 'That email is already in use.' : errMsg}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={close} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!dirty || !valid || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Same confirm-then-reveal-once flow as the Members page reset dialog: the
 * temp password is shown exactly once and the staffer must change it at next
 * login (`must_change_password` is set server-side).
 */
function ResetCashierPasswordDialog({
  target,
  branchId,
  onClose,
}: {
  target: BranchCashier | null;
  branchId: string;
  onClose: () => void;
}) {
  // Two modes: TYPE an exact password (default — no forced change), or GENERATE
  // a random temp password the staffer must rotate on next POS login.
  const [mode, setMode] = useState<'set' | 'generate'>('set');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // The temp-generate route is `user.password.reset`; the typed-set route is
  // `cashier.provision` (which the panel already holds). Hide the generate tab
  // for a role that only has the latter so it can't click into a 403.
  const canGenerateTemp = useHasPerm('user.password.reset');

  function readErr(err: unknown, fallback: string) {
    const code = (err as { response?: { data?: { error?: string; message?: string } } })
      ?.response?.data;
    return code?.message ?? code?.error ?? fallback;
  }

  const setMut = useMutation({
    mutationFn: () => setBranchCashierPassword(branchId, target!.id, pw),
    meta: { successMessage: 'Password set.' },
    onSuccess: () => close(),
    onError: (err: unknown) => setErrMsg(readErr(err, 'Failed to set password.')),
  });

  const genMut = useMutation({
    mutationFn: () => adminResetUserPassword(target!.id),
    meta: { successMessage: 'Password reset.' },
    onSuccess: (res) => {
      setTempPassword(res.tempPassword);
      setErrMsg(null);
    },
    onError: (err: unknown) => setErrMsg(readErr(err, 'Failed to reset password.')),
  });

  const pending = setMut.isPending || genMut.isPending;

  function close() {
    if (pending) return;
    setMode('set');
    setPw('');
    setConfirm('');
    setTempPassword(null);
    setErrMsg(null);
    setCopied(false);
    onClose();
  }

  function submitSet() {
    setErrMsg(null);
    if (pw.length < 8) return setErrMsg('Password must be at least 8 characters.');
    if (pw !== confirm) return setErrMsg('Passwords do not match.');
    setMut.mutate();
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
        </DialogHeader>
        {tempPassword ? (
          <div className="space-y-3">
            <p className="text-sm">
              Temp password for <span className="font-medium">{target?.email}</span>. Read this
              back to the staff member.{' '}
              <span className="font-medium text-destructive">It will not be shown again.</span>
            </p>
            <div className="rounded-md border bg-muted/30 p-3 font-mono text-lg tracking-widest text-center select-all">
              {tempPassword}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(tempPassword).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            {canGenerateTemp && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={mode === 'set' ? 'default' : 'outline'}
                  onClick={() => { setMode('set'); setErrMsg(null); }}
                  disabled={pending}
                >
                  Set a password
                </Button>
                <Button
                  size="sm"
                  variant={mode === 'generate' ? 'default' : 'outline'}
                  onClick={() => { setMode('generate'); setErrMsg(null); }}
                  disabled={pending}
                >
                  Generate temp password
                </Button>
              </div>
            )}

            {mode === 'set' ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Type a password for{' '}
                  <span className="font-medium text-foreground">{target?.email}</span>. They can log
                  in with it right away — no forced change.
                </p>
                <Input
                  type="password"
                  placeholder="New password (min 8)"
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                />
                <Input
                  type="password"
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                This generates a new temp password for{' '}
                <span className="font-medium text-foreground">{target?.email}</span>. They will be
                forced to change it on next POS login.
              </p>
            )}

            {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={pending}>
                Cancel
              </Button>
              {mode === 'set' ? (
                <Button onClick={submitSet} disabled={pending}>
                  {setMut.isPending ? 'Saving…' : 'Set password'}
                </Button>
              ) : (
                <Button onClick={() => genMut.mutate()} disabled={pending}>
                  {genMut.isPending ? 'Generating…' : 'Generate'}
                </Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
